import {useEffect, useLayoutEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import routes from "../../../../Routes/routes";
import {TABLES} from "../../../../Apis/Tables";
import {errorMsg} from "../../../../Apis/functions";
import {DeleteItemsApi} from "../../../../reduxToolkit/Commons/CommonsApi";
import {GUARD_ROSTER_PERMISSION, GUARDS_TAB_SEARCH, GUARDS_TABS, ZONE_ACCESS_PERMISSION} from "../Model/Model";

/**
 * Main screen of the guards module, the roster of the guards and the zone accesses they are
 * assigned to. The header creates and deletes the records of the tab displayed.
 */
export const GuardsMainApp = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const hasPermission = (permissionKey) => DEV || permission?.includes(permissionKey);
    // Each tab is shown with its own read permission
    const canReadRoster = hasPermission(GUARD_ROSTER_PERMISSION.READ);
    const canReadZoneAccess = hasPermission(ZONE_ACCESS_PERMISSION.READ);
    // Starts on the tab a zone access returns to, so the roster is not mounted and searched first
    const [activeStep, setActiveStep] = useState(
        location?.state?.currentTab ?? (canReadRoster ? GUARDS_TABS.ROSTER.INDEX : GUARDS_TABS.ZONE_ACCESS.INDEX)
    );
    const [selectedList, setSelectedList] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [modified, setModified] = useState(false);
    // Search of the tab displayed, shared with the header to export it and filter it
    const [searchElement, setSearchElement] = useState('');
    const [search, setSearch] = useState(false);
    const [bodyForExport, setBodyForExport] = useState(null);
    const [criteria, setCriteria] = useState(null);
    const [finalArray, setFinalArray] = useState([]);
    const [filterDialogShow, setFilterDialogShow] = useState(false);
    const {MODULE_ID: moduleId, OPTION: option} = GUARDS_TAB_SEARCH[activeStep];

    const steps = [
        canReadRoster && {label: t(GUARDS_TABS.ROSTER.TRANS), index: GUARDS_TABS.ROSTER.INDEX},
        canReadZoneAccess && {label: t(GUARDS_TABS.ZONE_ACCESS.TRANS), index: GUARDS_TABS.ZONE_ACCESS.INDEX},
    ].filter(Boolean);

    // The permissions are read again after login, the tab displayed moves to one that can still be read
    useEffect(() => {
        if (activeStep === GUARDS_TABS.ROSTER.INDEX && !canReadRoster && canReadZoneAccess) setActiveStep(GUARDS_TABS.ZONE_ACCESS.INDEX);
        if (activeStep === GUARDS_TABS.ZONE_ACCESS.INDEX && !canReadZoneAccess && canReadRoster) setActiveStep(GUARDS_TABS.ROSTER.INDEX);
    }, [canReadRoster, canReadZoneAccess]);

    /**
     * Validates the permission of a button of the header for the tab displayed.
     * @param {Object} list Permissions by tab, see TAB_PERMISSION_GUARDS.
     * @returns {boolean}
     */
    const permissionButtons = (list) => {
        const permissionRequired = list[activeStep];

        if (!permissionRequired) {
            return false;
        }
        return DEV || permission?.includes(permissionRequired);
    };

    // A zone access created or updated returns to its own tab
    useLayoutEffect(() => {
        if (location?.state?.currentTab !== undefined) {
            setActiveStep(location.state.currentTab);
            navigate(location.pathname, {replace: true, state: {}});
        }
    }, [location]);

    /**
     * Clears the simple and advanced search, each tab and each view starts without them.
     */
    const resetSearch = () => {
        setSearchElement('');
        setSearch(false);
        setBodyForExport(null);
        setCriteria(null);
        setFinalArray([]);
    };

    const handleStepChange = (step) => {
        setActiveStep(step);
        setSelectedList([]);
        resetSearch();
    };

    const goCreate = () => {
        navigate(activeStep === GUARDS_TABS.ZONE_ACCESS.INDEX ? routes.guards.zoneAccessCreate : routes.guards.create);
    };

    /**
     * Deletes the selected records of the tab displayed. A guard is deleted with its employee
     * record, the one its contract is kept in; a zone access with its zones and guards.
     */
    const deleteSelected = (ids) => {
        const tableName = activeStep === GUARDS_TABS.ZONE_ACCESS.INDEX ? TABLES.ZONE_ACCESS : TABLES.EMPLOYEES;

        dispatch(DeleteItemsApi({tableName, body: ids, showToast: false})).then(({payload: {data: {success, code}}}) => {
            if (success) {
                toast.success(t('has_been_deleted_successfully'));
                setSelectedList([]);
                setModified(true);
            } else errorMsg(code);
        });
    };

    return {
        t,
        navigate,
        DEV,
        permission,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        selectedList,
        setSelectedList,
        deleteModal,
        setDeleteModal,
        modified,
        setModified,
        hasPermission,
        goCreate,
        deleteSelected,
        canRead: canReadRoster || canReadZoneAccess,
        canReadRoster,
        canReadZoneAccess,
        permissionButtons,
        moduleId,
        option,
        searchElement,
        setSearchElement,
        search,
        setSearch,
        bodyForExport,
        setBodyForExport,
        criteria,
        setCriteria,
        finalArray,
        setFinalArray,
        filterDialogShow,
        setFilterDialogShow,
        resetSearch,
    };
};
